import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, Form, Image } from 'react-bootstrap';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/free-solid-svg-icons';

import { useLanguage } from '../contexts/LanguageContext';
import { useMode } from '../contexts/ModeContext';
import { useResto } from '../contexts/RestoContext';
import { useAuth } from '../contexts/AuthContext';
import { ucfirst } from '../utils/utils';
import config from '../config/config';
import { useWebsite, setFlashMessage } from '../contexts/WebsiteContext';

const Description = () => {
    const { t } = useTranslation();
    const { activeLanguage } = useLanguage();
    const { restoData, setRestoData } = useResto();
    const { isModeAdmin } = useMode();
    const { token } = useAuth();
    const { setIsLoading } = useWebsite();

    // ✅ SAFE fallback
    const availableLanguages = restoData?.availableLanguages || [];

    const { apiUrl } = config;
    const apiPath = `${apiUrl}resto`;

    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editedDescriptions, setEditedDescriptions] = useState({});

    useEffect(() => {
        if (
            restoData &&
            activeLanguage &&
            availableLanguages.length > 0
        ) {
            const editedDescriptionsCopy = {};

            availableLanguages.forEach(language => {
                const descriptionKey = `description${ucfirst(language)}`;
                editedDescriptionsCopy[descriptionKey] =
                    restoData?.[descriptionKey] || '';
            });

            setEditedDescriptions(editedDescriptionsCopy);
        }
    }, [activeLanguage, restoData, availableLanguages]);

    const handleOpenEditModal = () => {
        setEditModalOpen(true);
    };

    const handleCloseEditModal = () => {
        setEditModalOpen(false);
    };

    const handleEditSubmit = async () => {
        setIsLoading(true);

        try {
            const response = await axios.put(
                `${apiPath}/${restoData?.website}`,
                editedDescriptions,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            setRestoData(response.data);
            handleCloseEditModal();
            setFlashMessage(t('SUCCESS_UPDATED_MESSAGE'), 'success');
        } catch (error) {
            setFlashMessage(t('ERROR_MESSAGE'), 'error');
        }

        setIsLoading(false);
    };

    const handleEditInputChange = (e) => {
        const { name, value } = e.target;

        setEditedDescriptions(prevState => ({
            ...prevState,
            [name]: value,
        }));
    };

    return (
        <div>

            {!isModeAdmin && (
                <p>
                    {restoData &&
                        editedDescriptions[
                            `description${ucfirst(activeLanguage)}`
                        ]
                    }
                </p>
            )}

            {isModeAdmin && (
                <>
                    <h2>
                        Description
                        <Button
                            variant="primary"
                            className="btn-sm"
                            style={{ marginLeft: '10px' }}
                            onClick={handleOpenEditModal}
                        >
                            <FontAwesomeIcon icon={faEdit} />
                        </Button>
                    </h2>

                    {availableLanguages.map(language => (
                        <div key={language}>
                            <p>
                                <Image
                                    src={`${config.kubuniImagesUrl}general-icons/flag_${language}_icon_24.png`}
                                    className="flag-label"
                                    alt={language}
                                />
                                {' '}
                                {editedDescriptions[
                                    `description${ucfirst(language)}`
                                ] || ''}
                            </p>
                        </div>
                    ))}
                </>
            )}

            <Modal
                show={editModalOpen}
                onHide={handleCloseEditModal}
                size="xl"
            >
                <Modal.Header closeButton>
                    <Modal.Title>{t('EDIT')}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <Form>
                        {availableLanguages.map(language => (
                            <div key={language}>
                                <Form.Group
                                    controlId={`editDescription${ucfirst(language)}`}
                                >
                                    <Form.Label>
                                        <Image
                                            src={`${config.kubuniImagesUrl}general-icons/flag_${language}_icon_24.png`}
                                            className="flag-label"
                                            alt={language}
                                        />
                                    </Form.Label>

                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        name={`description${ucfirst(language)}`}
                                        value={
                                            editedDescriptions[
                                                `description${ucfirst(language)}`
                                            ] || ''
                                        }
                                        onChange={handleEditInputChange}
                                    />
                                </Form.Group>
                            </div>
                        ))}
                    </Form>
                </Modal.Body>

                <Modal.Footer className="justify-content-center">
                    <Button
                        variant="secondary"
                        onClick={handleCloseEditModal}
                    >
                        {t('CANCEL')}
                    </Button>

                    <Button
                        variant="primary"
                        onClick={handleEditSubmit}
                    >
                        {t('CONFIRM')}
                    </Button>
                </Modal.Footer>
            </Modal>

        </div>
    );
};

export default Description;